import { Link, useLocation } from 'react-router'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Package,
  ScrollText,
  MessageCircle,
  ClipboardCheck,
  DollarSign,
  Layout,
  Settings,
  LogOut,
  ShieldCheck,
  Gift,
  Activity,
  Mail,
  AlertOctagon,
  ClipboardList,
  MapPin,
  Bell,
} from 'lucide-react'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { useNotificationUnreadCount } from '@/hooks/useAdminNotifications'
import { formatBadgeCount } from '@/lib/notifications'
import { useAdminFeedbackUnreadCount } from '@/hooks/useAdminFeedback'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'

const navItems = [
  { title: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { title: 'Notifications', icon: Bell, path: '/notifications' },
  { title: 'Users', icon: Users, path: '/users' },
  { title: 'Revenue', icon: DollarSign, path: '/revenue' },
  { title: 'Subscriptions', icon: CreditCard, path: '/subscriptions' },
  { title: 'Gift Licenses', icon: Gift, path: '/gift-licenses' },
  { title: 'Disputes & Refunds', icon: AlertOctagon, path: '/billing' },
  { title: 'Feedback', icon: MessageCircle, path: '/feedback' },
  { title: 'QA Feedback', icon: ClipboardCheck, path: '/qa-feedback' },
  { title: 'Catalog', icon: Package, path: '/catalog' },
  { title: 'Locations', icon: MapPin, path: '/locations' },
  { title: 'Layouts', icon: Layout, path: '/layouts' },
  { title: 'System Health', icon: Activity, path: '/health' },
  { title: 'Ops Reviews', icon: ClipboardList, path: '/ops-reviews' },
  { title: 'Email Log', icon: Mail, path: '/emails' },
  { title: 'Settings', icon: Settings, path: '/settings' },
  { title: 'Audit Log', icon: ScrollText, path: '/audit' },
]

export function AdminSidebar() {
  const location = useLocation()
  const { user, adminRole, signOut } = useAdminAuth()
  const { data: unread } = useNotificationUnreadCount()
  const unreadBadge = formatBadgeCount(unread?.total ?? 0)
  const { data: unreadCount } = useAdminFeedbackUnreadCount()

  function isActive(path: string) {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Hangar<span className="text-primary">Max</span></span>
            <span className="text-xs text-muted-foreground">Admin Panel</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton asChild isActive={isActive(item.path)}>
                    <Link to={item.path}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.path === '/feedback' && (unreadCount ?? 0) > 0 && (
                    <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                      {unreadCount}
                    </SidebarMenuBadge>
                  )}
                  {item.path === '/notifications' && unreadBadge && (
                    <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                      {unreadBadge}
                    </SidebarMenuBadge>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="truncate text-xs text-muted-foreground">
              {user?.email}
            </span>
            <Badge variant="secondary" className="text-[10px]">
              {adminRole}
            </Badge>
          </div>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => signOut()}>
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
